import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, RefreshCw, Eye, EyeOff, X, FileText } from 'lucide-react';
import { RichTextEditor } from './RichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BlogTab = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: '', content: '', excerpt: '', keywords: '',
    category: '', featured_image: '', status: 'draft'
  });
  const [saving, setSaving] = useState(false);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/blog/admin/all`);
      setArticles(res.data);
    } catch {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const resetForm = () => {
    setForm({ title: '', content: '', excerpt: '', keywords: '', category: '', featured_image: '', status: 'draft' });
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (article) => {
    setForm({
      title: article.title,
      content: article.content || '',
      excerpt: article.excerpt || '',
      keywords: article.keywords || '',
      category: article.category || '',
      featured_image: article.featured_image || '',
      status: article.status,
    });
    setEditing(article);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/blog/admin/${editing.id}`, form);
        toast.success('Article updated');
      } else {
        await axios.post(`${API}/blog/admin`, form);
        toast.success('Article created');
      }
      resetForm();
      fetchArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (article) => {
    if (!window.confirm(`Delete "${article.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/blog/admin/${article.id}`);
      toast.success('Article deleted');
      fetchArticles();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const toggleStatus = async (article) => {
    const newStatus = article.status === 'published' ? 'draft' : 'published';
    try {
      await axios.put(`${API}/blog/admin/${article.id}`, { status: newStatus });
      toast.success(newStatus === 'published' ? 'Article published' : 'Article unpublished');
      fetchArticles();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const CATEGORIES = ['Industry News', 'Shipping Tips', 'Company Updates', 'Carrier Resources', 'Shipper Guides', 'Market Trends'];

  return (
    <div data-testid="blog-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-black text-zinc-900">Blog Articles</h2>
          <p className="text-sm text-zinc-500 mt-1">Manage articles for the public blog</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchArticles} className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 hover:border-zinc-300 transition-colors" data-testid="refresh-articles-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => { resetForm(); setShowForm(true); }} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors" data-testid="new-article-btn">
            <Plus className="w-4 h-4" /> New Article
          </button>
        </div>
      </div>

      {/* Editor Form */}
      {showForm && (
        <div className="bg-white border border-zinc-200 p-6 mb-6" data-testid="article-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-zinc-900">{editing ? 'Edit Article' : 'New Article'}</h3>
            <button onClick={resetForm} className="text-zinc-400 hover:text-zinc-600"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-zinc-700 mb-1">Title *</label>
                <input type="text" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-red-500"
                  placeholder="e.g., 10 Tips to Reduce Freight Shipping Costs in 2026"
                  data-testid="article-title-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-1">Category</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-red-500"
                  data-testid="article-category-select">
                  <option value="">Select category</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-1">Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-red-500"
                  data-testid="article-status-select">
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-zinc-700 mb-1">Excerpt</label>
                <textarea value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} rows={2}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-red-500 resize-none"
                  placeholder="Brief summary shown on the blog listing (auto-generated from content if left blank)"
                  data-testid="article-excerpt-input" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-zinc-700 mb-1">Content *</label>
                <RichTextEditor
                  key={editing?.id || 'new-article'}
                  value={form.content}
                  onChange={(html) => setForm((f) => ({ ...f, content: html }))}
                  placeholder="Write your article — use the toolbar to format text, insert links and images…"
                />
                <p className="text-[11px] text-zinc-500 mt-1">
                  Tip: Click the image icon to upload from your computer, or the small <strong>URL</strong> badge to paste an image link.
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-1">SEO Keywords</label>
                <input type="text" value={form.keywords} onChange={e => setForm({ ...form, keywords: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-red-500"
                  placeholder="freight shipping, trucking tips, supply chain (comma-separated)"
                  data-testid="article-keywords-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-1">Featured Image URL</label>
                <input type="text" value={form.featured_image} onChange={e => setForm({ ...form, featured_image: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-red-500"
                  placeholder="https://images.unsplash.com/..."
                  data-testid="article-image-input" />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={saving}
                className="bg-zinc-900 hover:bg-zinc-800 text-white px-6 py-2 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-50"
                data-testid="save-article-btn">
                {saving ? 'Saving...' : editing ? 'Update Article' : 'Create Article'}
              </button>
              <button type="button" onClick={resetForm} className="px-6 py-2 text-sm font-bold uppercase tracking-wide text-zinc-500 hover:text-zinc-700 border border-zinc-300">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Articles Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-20 text-zinc-400">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="font-medium">No articles yet</p>
          <p className="text-sm mt-1">Click "New Article" to write your first post</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="articles-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wide text-zinc-500 text-xs">Title</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wide text-zinc-500 text-xs">Category</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wide text-zinc-500 text-xs">Status</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wide text-zinc-500 text-xs">Date</th>
                <th className="text-right px-4 py-3 font-bold uppercase tracking-wide text-zinc-500 text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map(a => (
                <tr key={a.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`article-row-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-900 line-clamp-1">{a.title}</div>
                    <div className="text-xs text-zinc-400 mt-0.5">/blog/{a.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-zinc-500">{a.category || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold uppercase rounded ${
                      a.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {a.status === 'published' ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {a.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">
                    {a.published_at ? new Date(a.published_at).toLocaleDateString() : new Date(a.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => toggleStatus(a)} className="text-zinc-400 hover:text-zinc-700 p-1 mr-1" title={a.status === 'published' ? 'Unpublish' : 'Publish'}>
                      {a.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={() => handleEdit(a)} className="text-zinc-400 hover:text-zinc-700 p-1 mr-1" data-testid={`edit-article-${a.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(a)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`delete-article-${a.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
